export type BetaSignup = {
  email: string;
  ageGroup: "18-24" | "25-30" | "outside-target" | "";
  usResident: boolean;
  device: "iphone" | "android" | "laptop-webcam" | "desktop-webcam" | "other";
  display: "laptop" | "tv" | "both" | "neither";
  feedbackAvailability: "yes" | "maybe";
  consent: boolean;
};

export type BetaSignupField =
  "email" | "ageGroup" | "usResident" | "device" | "display" | "feedbackAvailability" | "consent";

export type BetaSignupErrors = Partial<Record<BetaSignupField, string>>;

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates only the minimum information needed to invite a person into P1.
 * No camera frames, pose data, health data, or gameplay recordings belong in
 * this payload.
 */
export function validateBetaSignup(signup: BetaSignup): BetaSignupErrors {
  const errors: BetaSignupErrors = {};

  if (!emailPattern.test(signup.email.trim())) {
    errors.email = "Enter a valid email address.";
  }
  if (signup.ageGroup === "") {
    errors.ageGroup = "Choose your age range.";
  } else if (signup.ageGroup === "outside-target") {
    errors.ageGroup = "This beta is currently for adults aged 18–30.";
  }
  if (!signup.usResident) {
    errors.usResident = "This beta is currently recruiting US residents.";
  }
  if (!signup.device) {
    errors.device = "Choose the device you would use for the beta.";
  }
  if (!signup.display) {
    errors.display = "Tell us whether you have a laptop or TV for the game screen.";
  }
  if (!signup.feedbackAvailability) {
    errors.feedbackAvailability = "Choose whether you can send short feedback.";
  }
  if (!signup.consent) {
    errors.consent = "Confirm that we may email you about the beta.";
  }

  return errors;
}

/**
 * Sends a beta application to an operator-configured, HTTPS-only endpoint.
 * The endpoint is intentionally external: this static app has no account or
 * database layer, and a host must configure its own privacy policy/retention.
 */
export async function submitBetaSignup(
  signup: BetaSignup,
  endpoint: string,
  request: typeof fetch = fetch,
): Promise<void> {
  const url = new URL(endpoint);
  if (url.protocol !== "https:") {
    throw new Error("Beta sign-up endpoint must use HTTPS.");
  }

  const response = await request(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: signup.email.trim(),
      ageGroup: signup.ageGroup,
      usResident: signup.usResident,
      device: signup.device,
      display: signup.display,
      feedbackAvailability: signup.feedbackAvailability,
      consent: signup.consent,
      source: "dance-game-beta-landing",
    }),
  });

  if (!response.ok) {
    throw new Error("The beta form could not be submitted. Please try again later.");
  }
}
